import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Shield, Briefcase, Users, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const RoleBasedLogin = () => {
  const navigate = useNavigate();
  const { login, staffLogin } = useAuth();
  
  const [selectedRole, setSelectedRole] = useState('OWNER');
  const [credentials, setCredentials] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Staff uses a different login endpoint
      if (selectedRole === 'STAFF') {
        const result = await staffLogin(credentials.email, credentials.password);
        
        if (!result.success) {
          toast.error(result.error || 'Staff login failed');
          setLoading(false);
          return;
        }

        const { businesses, staff } = result;
        
        // Redirect to staff dashboard
        if (businesses && businesses.length > 0) {
          toast.success(`Welcome, ${staff?.full_name || 'Staff'}!`);
          navigate(`/business/${businesses[0].id}/dashboard/staff`);
        } else {
          toast.error('No business assigned to this staff account');
        }
        setLoading(false);
        return;
      }

      // Owner and Admin use standard login
      const result = await login(credentials.email, credentials.password);
      
      if (!result.success) {
        toast.error(result.error || 'Login failed');
        setLoading(false);
        return;
      }

      const { user, businesses } = result;

      // Validate role matches selected login type
      let userRole = null;
      
      if (selectedRole === 'ADMIN') {
        // Check if platform super admin
        const isPlatformAdmin = user.platform_role === 'SUPER_ADMIN' ||
                       user.platform_role === 'ADMIN' ||
                       user.email === 'admin@aibizbook.com' ||
                       user.email === 'admin@billsera.com' ||
                       user.email === 'admin@test.com';

        // Check if business-level admin
        const adminBusiness = businesses && businesses.find(b => b.user_role === 'ADMIN');

        if (isPlatformAdmin) {
          userRole = 'ADMIN';
          user.platform_role = 'SUPER_ADMIN';
          navigate('/admin/dashboard');
        } else if (adminBusiness) {
          // Business-level admin — treat like owner, go to business dashboard
          userRole = 'ADMIN';
          navigate(`/business/${adminBusiness.id}/dashboard/owner`);
        } else {
          toast.error('This login is only for Platform Admins. Please use Owner or Staff login.');
          setLoading(false);
          return;
        }
      } else if (selectedRole === 'OWNER') {
        // Allow any business member (all roles)
        const isBusinessMember = businesses && businesses.length > 0;

        if (isBusinessMember) {
          const biz = businesses[0];
          const role = biz.user_role?.toUpperCase();
          userRole = role || 'OWNER';

          if (businesses.length > 1) {
            navigate('/select-business');
          } else if (role === 'STAFF') {
            navigate(`/business/${biz.id}/dashboard/staff`);
          } else {
            navigate(`/business/${biz.id}/dashboard/owner`);
          }
        } else {
          toast.error('No business access found.');
          setLoading(false);
          return;
        }
      }
      // Note: STAFF login is handled separately above with staffLogin()

    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const roleConfig = {
    ADMIN: {
      icon: Shield,
      title: 'Admin Login',
      description: 'Platform administrators only',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
    },
    OWNER: {
      icon: Briefcase,
      title: 'Business Owner Login',
      description: 'Shop owners and managers',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    STAFF: {
      icon: Users,
      title: 'Staff Login',
      description: 'Team members and employees',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-1">
          <div className="flex justify-center">
            <img
              src="/Billsera.png"
              alt="Billsera"
              className="w-48 h-auto -my-6"
              style={{ maxHeight: '140px' }}
            />
          </div>
          <CardDescription>Select your role and login</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={selectedRole} onValueChange={setSelectedRole}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="ADMIN" className="text-xs">Admin</TabsTrigger>
              <TabsTrigger value="OWNER" className="text-xs">Owner</TabsTrigger>
              <TabsTrigger value="STAFF" className="text-xs">Staff</TabsTrigger>
            </TabsList>

            {['ADMIN', 'OWNER', 'STAFF'].map((role) => {
              const config = roleConfig[role];
              const Icon = config.icon;
              
              return (
                <TabsContent key={role} value={role} className="space-y-4 mt-4">
                  <div className={`flex items-center gap-3 p-4 rounded-lg ${config.bgColor}`}>
                    <Icon className={`w-8 h-8 ${config.color}`} />
                    <div>
                      <div className={`font-semibold ${config.color}`}>{config.title}</div>
                      <div className="text-sm text-gray-600">{config.description}</div>
                    </div>
                  </div>

                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <Label>Email / Phone</Label>
                      <Input
                        type="text"
                        placeholder="Enter email or phone"
                        value={credentials.email}
                        onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                        required
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <Label>Password</Label>
                      <Input
                        type="password"
                        placeholder="Enter password"
                        value={credentials.password}
                        onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                        required
                        disabled={loading}
                      />
                      <div className="flex justify-end mt-1.5">
                        <Link
                          to="/forgot-password"
                          className="text-xs text-slate-500 hover:text-slate-700"
                        >
                          Forgot password?
                        </Link>
                      </div>
                    </div>

                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? 'Logging in...' : `Login as ${config.title.replace(' Login', '')}`}
                    </Button>
                  </form>

                  <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-800">
                      Only {role === 'ADMIN' ? 'administrators' : role === 'OWNER' ? 'business owners' : 'staff members'} can login here. 
                      Using wrong role will result in error.
                    </p>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default RoleBasedLogin;
